import * as THREE from 'three';
import { GeometryBuilder } from './geometry-builder';
import { characterJoint } from './character-joint';
import { fieldRifle } from './field-equipment';
import type { EntityPart } from '../core/entities';

export interface CharacterStyle {
  cut: number;
  hair: number;
  head: number;
  build: number;
  accessory: number;
  signature: string;
  skin: number;
  hairColor: number;
  trousers: number;
}
const SKIN = [0xe5be9a, 0xc79773, 0x9f7053, 0x6c4939, 0xd5a783, 0xaf825f];
const HAIR = [0x302f2b, 0x665142, 0xa57a45, 0xc0b59a, 0x754c35, 0x42423e];
const TROUSERS = [0x34464e, 0x596560, 0x71685b, 0x475969, 0x655858, 0x7c8177];
const HEAD_WIDTH = [0.235, 0.29, 0.21, 0.27];
const HEAD_HEIGHT = [0.29, 0.27, 0.32, 0.26];
const SIDES = [
  ['left', -1],
  ['right', 1],
] as const;
const OFFSETS = [0, Math.PI];
const HOLSTERED = new Set([
  'parachuting',
  'surrendering',
  'planting',
  'operating aircraft',
  'leaving combat',
]);
const WALKING = new Set(['walk', 'flee', 'yielding', 'roam']);
const WORKING = new Set([
  'inspecting rescue equipment',
  'clearing rubble',
  'working',
  'building',
  'inspecting',
  'browsing',
  'purchasing',
  'collecting',
  'unloading',
  'restocking',
  'preparing a meal',
  'serving customers',
  'planting',
  'operating aircraft',
]);
const CHARACTER_SHAPES = 6 * 8 * 4 * 4 * 4;
export const CHARACTER_COMBINATIONS = CHARACTER_SHAPES * SKIN.length;
export function characterStyle(index: number, seed = 0): CharacterStyle {
  let n = (((index * 1597 + seed * 43) % CHARACTER_SHAPES) + CHARACTER_SHAPES) % CHARACTER_SHAPES;
  const cut = n % 6;
  n = Math.floor(n / 6);
  const hair = n % 8;
  n = Math.floor(n / 8);
  const head = n % 4;
  n = Math.floor(n / 4);
  const build = n % 4;
  n = Math.floor(n / 4);
  const accessory = n % 4;
  const skin = (Math.abs(index * 7 + seed) + Math.floor(index / CHARACTER_SHAPES)) % SKIN.length;
  return {
    cut,
    hair,
    head,
    build,
    accessory,
    signature: `${cut}/${hair}/${head}/${build}/${accessory}/${skin}`,
    skin: SKIN[skin],
    hairColor: HAIR[Math.abs(index * 11 + seed) % HAIR.length],
    trousers: TROUSERS[Math.abs(index * 5 + seed) % TROUSERS.length],
  };
}

/** Chamfered cross sections give shoulders, cheeks and clothes readable edges. */
function prism(
  g: GeometryBuilder,
  y: number,
  h: number,
  bottomW: number,
  topW: number,
  depth: number,
  color = 0xffffff,
  x = 0,
  z = 0,
  bevel = 0.16,
): void {
  const ring = (w: number, yy: number) =>
    [
      [-w / 2 + w * bevel, -depth / 2],
      [w / 2 - w * bevel, -depth / 2],
      [w / 2, -depth / 2 + depth * bevel],
      [w / 2, depth / 2 - depth * bevel],
      [w / 2 - w * bevel, depth / 2],
      [-w / 2 + w * bevel, depth / 2],
      [-w / 2, depth / 2 - depth * bevel],
      [-w / 2, -depth / 2 + depth * bevel],
    ].map(([xx, zz]) => ({ x: x + xx, y: yy, z: z + zz }));
  const a = ring(bottomW, y),
    b = ring(topW, y + h);
  for (let i = 0; i < 8; i++) {
    const j = (i + 1) % 8,
      normal = new THREE.Vector3()
        .subVectors(b[i], a[i])
        .cross(new THREE.Vector3().subVectors(a[j], a[i]))
        .normalize();
    g.quad(a[i], b[i], b[j], a[j], normal, color);
    g.triangle({ x, y, z }, a[i], a[j], { x: 0, y: -1, z: 0 }, color);
    g.triangle({ x, y: y + h, z }, b[j], b[i], { x: 0, y: 1, z: 0 }, color);
  }
}

export function characterGeometries(): Map<string, THREE.BufferGeometry> {
  const result = new Map<string, THREE.BufferGeometry>();
  const add = (name: string, build: (g: GeometryBuilder) => void) => {
    const g = new GeometryBuilder();
    build(g);
    const geometry = g.geometry();
    geometry.computeBoundingBox();
    result.set(name, geometry);
  };
  for (let cut = 0; cut < 6; cut++)
    add(`torso-${cut}`, (g) => {
      const hem = cut === 2 ? -0.22 : cut === 5 ? -0.3 : 0;
      prism(
        g,
        hem,
        0.46 - hem,
        cut === 5 ? 0.54 : cut === 2 ? 0.43 : 0.34,
        cut === 1 ? 0.45 : 0.41,
        cut === 5 ? 0.34 : 0.25,
      );
      if (cut === 1 || cut === 4) {
        g.box(-0.13, 0.16, -0.134, 0.26, 0.27, 0.025, 0x526069);
        for (const x of [-0.16, 0.08]) g.box(x, 0.15, -0.152, 0.08, 0.25, 0.026, 0xffffff);
        for (const x of [-0.16, 0.07]) g.box(x, 0.03, -0.143, 0.09, 0.055, 0.024, 0xb5b8ad);
      }
      if (cut === 2) {
        g.box(-0.028, -0.18, -0.137, 0.055, 0.59, 0.025, 0x5b6666);
        g.box(-0.22, 0.02, -0.148, 0.44, 0.055, 0.025, 0x8b9086);
      }
      if (cut === 3) {
        prism(g, 0.33, 0.19, 0.31, 0.24, 0.2, 0xffffff, 0, 0.12);
        g.box(-0.12, 0.04, -0.143, 0.24, 0.1, 0.035, 0xc1c4bc);
      }
      if (cut === 0) g.box(-0.1, 0.415, -0.14, 0.2, 0.04, 0.023, 0x71817c);
      if (cut === 5) g.box(-0.18, 0.04, -0.142, 0.36, 0.035, 0.025, 0x76887e);
    });
  for (let head = 0; head < 4; head++)
    add(`head-${head}`, (g) => {
      const w = HEAD_WIDTH[head],
        h = HEAD_HEIGHT[head];
      prism(
        g,
        0.018,
        h,
        w * [0.8, 0.95, 0.72, 0.9][head],
        w,
        0.235,
        0xffffff,
        0,
        0,
        [0.23, 0.12, 0.24, 0.32][head],
      );
      prism(g, -0.045, 0.065, 0.1, 0.11, 0.105);
      for (const x of [-0.07, 0.055]) g.box(x, 0.178, -0.124, 0.025, 0.023, 0.015, 0x292e2b);
      g.box(-0.022, 0.118, -0.144, 0.044, 0.055, 0.045, 0xffffff);
      g.box(-0.036, 0.075, -0.121, 0.072, 0.009, 0.012, 0x966f5e);
      for (const sign of [-1, 1])
        g.box(sign > 0 ? w / 2 - 0.01 : -w / 2 - 0.025, 0.115, -0.012, 0.035, 0.077, 0.065, 0xffffff);
    });
  for (let hair = 0; hair < 8; hair++)
    add(`hair-${hair}`, (g) => {
      const top = hair === 6 ? 0.38 : hair === 5 ? 0.335 : 0.315;
      prism(g, 0.235, top - 0.235, 0.277, hair === 6 ? 0.25 : 0.248, 0.258, 0xffffff, 0, 0.015, 0.2);
      if (hair === 1) g.box(-0.145, 0.205, -0.132, 0.08, 0.14, 0.06, 0xffffff);
      if ([2, 3, 4, 5].includes(hair)) {
        g.box(-0.14, hair === 3 ? -0.09 : 0.06, 0.09, 0.28, hair === 3 ? 0.37 : 0.21, 0.095, 0xffffff);
        if (hair === 2 || hair === 3)
          for (const x of [-0.148, 0.112]) g.box(x, 0.06, -0.07, 0.036, 0.21, 0.16, 0xffffff);
      }
      if (hair === 4) prism(g, -0.015, 0.3, 0.12, 0.09, 0.115, 0xffffff, 0, 0.195);
      if (hair === 5) prism(g, 0.18, 0.15, 0.135, 0.115, 0.14, 0xffffff, 0, 0.19);
      if (hair === 6)
        for (const [x, z] of [
          [-0.1, 0],
          [0.1, 0],
          [0, 0.105],
          [0, -0.1],
        ])
          prism(g, 0.285, 0.08, 0.13, 0.11, 0.12, 0xffffff, x, z);
      if (hair === 7) {
        prism(g, 0.25, 0.09, 0.285, 0.22, 0.275);
        g.box(-0.15, 0.243, -0.25, 0.3, 0.028, 0.16, 0xffffff);
      }
    });
  add('upper-arm', (g) => prism(g, -0.275, 0.275, 0.103, 0.132, 0.14));
  add('forearm', (g) => prism(g, -0.25, 0.25, 0.076, 0.097, 0.104));
  add('hand', (g) => prism(g, -0.1, 0.11, 0.085, 0.077, 0.085));
  add('thigh', (g) => prism(g, -0.37, 0.37, 0.126, 0.156, 0.175));
  add('shin', (g) => prism(g, -0.37, 0.37, 0.098, 0.123, 0.145));
  add('shoe', (g) => {
    prism(g, -0.012, 0.095, 0.123, 0.119, 0.245, 0xffffff, 0, -0.058, 0.13);
    g.box(-0.066, -0.018, -0.18, 0.132, 0.026, 0.25, 0x444d4b);
  });
  add('scarf', (g) => {
    prism(g, 0.355, 0.09, 0.29, 0.29, 0.3);
    g.box(-0.1, 0.06, -0.165, 0.09, 0.32, 0.052, 0xffffff);
  });
  add('backpack', (g) => {
    prism(g, -0.07, 0.38, 0.3, 0.265, 0.17, 0xffffff, 0, 0.22);
    for (const x of [-0.15, 0.12]) g.box(x, 0.05, -0.14, 0.03, 0.35, 0.027, 0x465d5c);
    g.box(-0.11, -0.02, 0.297, 0.22, 0.13, 0.025, 0x70847d);
  });
  add('satchel', (g) => {
    prism(g, -0.15, 0.25, 0.17, 0.18, 0.2, 0xffffff, -0.27, 0);
    for (let i = 0; i < 7; i++)
      g.box(-0.18 + i * 0.049, 0.04 + i * 0.048, -0.151, 0.06, 0.045, 0.018, 0x656f63);
  });
  add('glasses', (g) => {
    for (const x of [-0.11, 0.014]) {
      g.box(x, 0.153, -0.139, 0.096, 0.063, 0.018, 0x32423f);
      g.box(x + 0.012, 0.164, -0.149, 0.072, 0.042, 0.012, 0xb0c5bf);
    }
    g.box(-0.018, 0.177, -0.149, 0.036, 0.014, 0.014, 0x32423f);
  });
  add('military-tunic', (g) => {
    prism(g, -0.07, 0.54, 0.39, 0.43, 0.27);
    g.box(-0.12, 0.41, -0.146, 0.24, 0.03, 0.02, 0x79876a);
  });
  for (const faction of ['friendly', 'hostile', 'elite'] as const) {
    const cloth = faction === 'elite' ? 0x1b222a : faction === 'friendly' ? 0x687e55 : 0x89918f;
    add(`helmet-${faction}`, (g) => {
      prism(g, 0.185, 0.16, 0.34, 0.265, 0.325, cloth, 0, 0.01, 0.24);
      g.box(-0.17, 0.177, -0.18, 0.34, 0.033, 0.36, cloth);
      for (const x of [-0.129, 0.117]) g.box(x, 0.025, -0.032, 0.012, 0.18, 0.019, 0x394c3b);
      g.box(-0.06, 0.253, -0.166, 0.12, 0.05, 0.017, faction === 'friendly' ? 0xe1ddad : 0xc3cad0);
      if (faction === 'hostile') g.box(-0.095, 0.209, -0.182, 0.19, 0.055, 0.019, 0x48565f);
      if (faction === 'elite') {
        g.box(-0.07, 0.235, -0.19, 0.14, 0.05, 0.05, 0x10161c);
        g.box(-0.025, 0.16, -0.22, 0.05, 0.11, 0.045, 0x303b43);
        for (const x of [-0.09, 0.025]) {
          g.box(x, 0.12, -0.27, 0.065, 0.066, 0.105, 0x161d24);
          g.box(x + 0.009, 0.13, -0.276, 0.047, 0.045, 0.008, 0x375e51);
        }
      }
    });
    add(`vest-${faction}`, (g) => {
      prism(g, -0.02, 0.39, 0.4, 0.43, 0.32, cloth, 0, -0.015);
      for (const x of [-0.17, -0.057, 0.056])
        g.box(x, 0.035, -0.226, 0.1, 0.16, 0.066, faction === 'elite' ? 0x29333b : 0x556750);
      for (const x of [-0.205, 0.162]) g.box(x, 0.27, -0.193, 0.043, 0.2, 0.04, cloth);
      g.box(-0.145, 0.03, 0.16, 0.29, 0.28, 0.1, cloth);
      g.box(-0.08, 0.265, -0.184, 0.16, 0.062, 0.027, faction === 'friendly' ? 0xe0d8a4 : 0xbdc5c9);
      g.box(0.2, -0.02, -0.07, 0.075, 0.18, 0.16, 0x677a64);
      g.box(-0.2, 0.29, 0.12, 0.07, 0.13, 0.075, 0x252e32);
      g.box(-0.184, 0.39, 0.142, 0.018, 0.24, 0.018, 0x242b2e);
    });
  }
  add('combat-boot', (g) => {
    prism(g, -0.015, 0.18, 0.15, 0.126, 0.27, 0x3d4a40, 0, -0.055);
    g.box(-0.079, -0.025, -0.199, 0.158, 0.033, 0.288, 0x29342f);
    for (let i = 0; i < 3; i++) g.box(-0.048, 0.071 + i * 0.028, -0.194, 0.096, 0.01, 0.016, 0x7e8a72);
  });
  add('field-rifle', (g) => fieldRifle(g));
  add('elite-rifle', (g) => fieldRifle(g, true));
  return result;
}

export interface CharacterMotion {
  phase: number;
  previousPhase: number;
  stride: number;
  previousStride: number;
  x: number;
  z: number;
  initialized: boolean;
}
export const createCharacterMotion = (): CharacterMotion => ({
  phase: 0,
  previousPhase: 0,
  stride: 0,
  previousStride: 0,
  x: 0,
  z: 0,
  initialized: false,
});
export function advanceCharacterMotion(
  m: CharacterMotion,
  point: Pick<THREE.Vector3, 'x' | 'z'>,
  dt: number,
  speed: number,
  state: string,
  hidden = false,
): void {
  const distance = m.initialized ? Math.hypot(point.x - m.x, point.z - m.z) : 0;
  const walking = !hidden && WALKING.has(state) && distance < Math.max(0.3, speed * dt * 2.5);
  m.previousPhase = m.phase;
  m.previousStride = m.stride;
  if (walking) m.phase += (distance * Math.PI * 2) / 1.24;
  m.stride = walking ? Math.min(1, distance / Math.max(0.001, dt * speed)) : 0;
  m.x = point.x;
  m.z = point.z;
  m.initialized = true;
}
export const CHARACTER_PARTS = [
  'torso',
  'head',
  'hair',
  'accessory',
  'upper-arm:left',
  'forearm:left',
  'hand:left',
  'upper-arm:right',
  'forearm:right',
  'hand:right',
  'thigh:left',
  'shin:left',
  'shoe:left',
  'thigh:right',
  'shin:right',
  'shoe:right',
  'weapon',
] as const;
export type CharacterPart = (typeof CHARACTER_PARTS)[number];
interface Part {
  submitted?: { index: number; version: number; color: number };
  mesh?: THREE.InstancedMesh;
  cloth?: boolean;
  tint?: THREE.Color;
  name: CharacterPart;
  geometry: string;
  color: number;
}
interface CharacterEntry {
  coarse?: { parts: Part[]; matrices: Record<string, THREE.Matrix4>; version: number };
  military?: 'friendly' | 'hostile';
  poseVersion: number;
  style: CharacterStyle;
  motion: CharacterMotion;
  matrices: Record<CharacterPart, THREE.Matrix4>;
  parts: Part[];
  bounds: EntityPart[];
  root: THREE.Matrix4;
  translation: THREE.Vector3;
  alpha: number;
  time: number;
  state: string;
  carrying: boolean;
  seatHeight?: number;
  seatBlend?: number;
  dirty: boolean;
  samplePhase: number;
  sampleStride: number;
}
const BODY_SCALE = [
  [0.9, 1, 0.94],
  [1.12, 0.98, 1.05],
  [1, 0.91, 1],
  [1.05, 1.03, 1.18],
] as const;
const ROOT_BOUNDS = new THREE.Box3(
  new THREE.Vector3(-0.63, -0.12, -0.85),
  new THREE.Vector3(0.63, 1.82, 0.6),
);

/** Shared part meshes and local joints; no unique material/skeleton per person. */
export class CharacterRenderer {
  readonly meshes = new Map<string, THREE.InstancedMesh>();
  readonly entries = new Map<string, CharacterEntry>();
  readonly geometry = characterGeometries();
  readonly bounds = ROOT_BOUNDS;
  posedThisFrame = 0;
  translatedThisFrame = 0;
  coarseThisFrame = 0;
  matrixWritesThisFrame = 0;
  private used = new Set<string>();
  private colors = new Map<number, THREE.Color>();
  private rifleArms = new Map<string, [CharacterPart, THREE.Matrix4][]>();
  private submissions = new Map<
    THREE.InstancedMesh,
    {
      owners: Part[];
      matrixMin: number;
      matrixMax: number;
      colorMin: number;
      colorMax: number;
    }
  >();
  private posing?: CharacterEntry;
  private scale = new THREE.Vector3();
  private local = new THREE.Matrix4();
  constructor(
    readonly scene: THREE.Scene,
    material: THREE.Material | THREE.Material[],
    readonly seed: number,
  ) {
    // At a few pixels tall, retain clothes, face, hair, legs and rifle as a
    // compact silhouette. These are presentation meshes only; all articulated
    // hit/attachment joints remain available through pose() on the original ID.
    for (const name of [...Array.from({ length: 6 }, (_, cut) => `torso-${cut}`), 'military-tunic']) {
      const source = this.geometry.get(name)!,
        g = new GeometryBuilder();
      g.positions = Array.from(source.attributes.position.array);
      g.normals = Array.from(source.attributes.normal.array);
      g.colors = Array.from(source.attributes.color.array);
      for (const x of [-0.35, 0.23]) g.box(x, -0.12, -0.07, 0.12, 0.5, 0.14, 0xffffff);
      this.geometry.set(`distant-${name}`, g.geometry());
    }
    const leg = new GeometryBuilder();
    leg.box(-0.067, -0.75, -0.075, 0.134, 0.75, 0.15, 0xffffff);
    leg.box(-0.074, -0.825, -0.17, 0.148, 0.075, 0.25, 0x35393b);
    this.geometry.set('distant-leg', leg.geometry());
    for (const [key, geometry] of this.geometry) {
      const mesh = new THREE.InstancedMesh(geometry, material, 8192);
      mesh.count = 0;
      mesh.castShadow = mesh.receiveShadow = true;
      mesh.frustumCulled = false;
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      this.meshes.set(key, mesh);
      this.submissions.set(mesh, {
        owners: [],
        matrixMin: Infinity,
        matrixMax: -1,
        colorMin: Infinity,
        colorMax: -1,
      });
      scene.add(mesh);
    }
  }
  ensure(id: string): CharacterEntry {
    const prior = this.entries.get(id);
    if (prior) return prior;
    let hash = 2166136261;
    for (const char of id) hash = Math.imul(hash ^ char.charCodeAt(0), 16777619) >>> 0;
    const first =
      (hash % CHARACTER_SHAPES) +
      (id.startsWith('crew:military:') ? (id.includes(':hostile:') ? 2 : 1) * CHARACTER_SHAPES : 0);
    let style = characterStyle(first, this.seed),
      tries = 0;
    while (this.used.has(style.signature) && tries < CHARACTER_COMBINATIONS)
      style = characterStyle((first + ++tries) % CHARACTER_COMBINATIONS, this.seed);
    if (this.used.has(style.signature)) throw new Error('Character appearance capacity exceeded');
    this.used.add(style.signature);
    const parts: Part[] = [
      { name: 'torso', geometry: `torso-${style.cut}`, color: 0xffffff },
      { name: 'head', geometry: `head-${style.head}`, color: style.skin },
      { name: 'hair', geometry: `hair-${style.hair}`, color: style.hairColor },
      {
        name: 'accessory',
        geometry: ['scarf', 'backpack', 'satchel', 'glasses'][style.accessory],
        color: style.accessory === 3 ? 0xffffff : 0x9ba99b,
      },
    ];
    for (const side of ['left', 'right'] as const)
      for (const name of ['upper-arm', 'forearm', 'hand', 'thigh', 'shin', 'shoe'] as const)
        parts.push({
          name: `${name}:${side}`,
          geometry: name,
          color: ['forearm', 'hand'].includes(name)
            ? style.skin
            : name === 'upper-arm'
              ? 0xffffff
              : name === 'shoe'
                ? 0x3a4847
                : style.trousers,
        });
    const military = id.startsWith('crew:military:')
      ? id.includes(':hostile:')
        ? 'hostile'
        : 'friendly'
      : undefined;
    if (military) {
      const elite = id.includes(':elite:');
      for (const part of parts) {
        if (part.name === 'torso') part.geometry = 'military-tunic';
        if (part.name === 'hair') {
          part.geometry = `helmet-${elite ? 'elite' : military}`;
          part.color = 0xffffff;
        }
        if (part.name === 'accessory') {
          part.geometry = `vest-${elite ? 'elite' : military}`;
          part.color = 0xffffff;
        }
        if (part.name.startsWith('shoe:')) {
          part.geometry = 'combat-boot';
          part.color = elite ? 0x3b3e49 : 0xffffff;
        }
        if (part.name.startsWith('thigh:') || part.name.startsWith('shin:'))
          part.color = elite ? 0x202830 : military === 'friendly' ? 0x667c52 : 0x88938d;
        if (elite && part.name === 'head') part.color = 0x303438;
        if (part.name.startsWith('hand:')) part.color = elite ? 0x20262b : 0x394138;
      }
      parts.push({ name: 'weapon', geometry: elite ? 'elite-rifle' : 'field-rifle', color: 0xffffff });
    }
    for (const part of parts) {
      part.mesh = this.meshes.get(part.geometry)!;
      part.cloth =
        part.name === 'torso' ||
        part.name.startsWith('upper-arm:') ||
        (part.name.startsWith('forearm:') && (!!military || (style.cut >= 1 && style.cut <= 3)));
      part.tint = this.tint(part.color);
    }
    const matrices = Object.fromEntries(CHARACTER_PARTS.map((p) => [p, new THREE.Matrix4()])) as Record<
      CharacterPart,
      THREE.Matrix4
    >;
    const entry: CharacterEntry = {
      military,
      poseVersion: 0,
      style,
      parts,
      matrices,
      motion: createCharacterMotion(),
      root: new THREE.Matrix4(),
      translation: new THREE.Vector3(),
      alpha: 1,
      time: 0,
      state: 'wait',
      carrying: false,
      dirty: true,
      samplePhase: NaN,
      sampleStride: NaN,
      bounds: parts.map((p) => ({ anchor: p.name, bounds: this.geometry.get(p.geometry)!.boundingBox! })),
    };
    this.entries.set(id, entry);
    return entry;
  }
  retain(ids: Set<string>): void {
    for (const [id, e] of this.entries)
      if (!ids.has(id)) {
        this.used.delete(e.style.signature);
        this.entries.delete(id);
      }
  }
  pose(id: string, anchor = 'root'): THREE.Matrix4 | undefined {
    const e = this.entries.get(id);
    if (!e) return;
    if (anchor === 'root') return e.root;
    this.updatePose(e);
    return e.matrices[anchor as CharacterPart];
  }
  begin(): void {
    this.posedThisFrame = 0;
    this.translatedThisFrame = 0;
    this.coarseThisFrame = this.matrixWritesThisFrame = 0;
    for (const m of this.meshes.values()) m.count = 0;
    for (const s of this.submissions.values()) {
      s.matrixMin = s.colorMin = Infinity;
      s.matrixMax = s.colorMax = -1;
    }
  }
  private joint(name: CharacterPart, x: number, y: number, z: number, angle = 0): void {
    const e = this.posing!;
    characterJoint(e.matrices[name], e.root, BODY_SCALE[e.style.build], x, y, z, angle);
  }
  private tint(hex: number): THREE.Color {
    let tint = this.colors.get(hex);
    if (!tint) {
      tint = new THREE.Color(hex);
      this.colors.set(hex, tint);
    }
    return tint;
  }
  render(
    id: string,
    root: THREE.Matrix4,
    alpha: number,
    time: number,
    state: string,
    carrying: boolean,
    shirt: number,
    visible: boolean,
    seatHeight?: number,
    seatBlend?: number,
    coarse = false,
  ): void {
    const e = this.ensure(id);
    const phase = e.motion.previousPhase + (e.motion.phase - e.motion.previousPhase) * alpha,
      stride = e.motion.previousStride + (e.motion.stride - e.motion.previousStride) * alpha;
    const previous = e.root.elements,
      current = root.elements;
    e.dirty ||=
      previous[0] !== current[0] ||
      previous[1] !== current[1] ||
      previous[2] !== current[2] ||
      previous[4] !== current[4] ||
      previous[5] !== current[5] ||
      previous[6] !== current[6] ||
      previous[8] !== current[8] ||
      previous[9] !== current[9] ||
      previous[10] !== current[10] ||
      e.samplePhase !== phase ||
      e.sampleStride !== stride ||
      e.state !== state ||
      e.carrying !== carrying ||
      e.seatHeight !== seatHeight ||
      e.seatBlend !== seatBlend ||
      ((WORKING.has(state) || state === 'stretching' || state === 'firing') && e.time !== time);
    e.translation.x += current[12] - previous[12];
    e.translation.y += current[13] - previous[13];
    e.translation.z += current[14] - previous[14];
    e.samplePhase = phase;
    e.sampleStride = stride;
    e.root.copy(root);
    e.alpha = alpha;
    e.time = time;
    e.state = state;
    e.carrying = carrying;
    e.seatHeight = seatHeight;
    e.seatBlend = seatBlend;
    if (!visible) return;
    if (coarse) {
      this.renderCoarse(e, shirt);
      return;
    }
    this.updatePose(e);
    this.submit(e.parts, e.matrices, e.poseVersion, shirt);
  }
  private submit(
    parts: Part[],
    matrices: Record<string, THREE.Matrix4>,
    version: number,
    shirt: number,
  ): void {
    const shirtTint = this.tint(shirt);
    for (const part of parts) {
      const mesh = part.mesh!;
      const index = mesh.count++;
      if (index >= mesh.instanceMatrix.count) throw new Error('Character instance capacity exceeded');
      const slots = this.submissions.get(mesh)!,
        prior = part.submitted,
        replaced = slots.owners[index] !== part || prior?.index !== index,
        color = part.cloth ? shirt : part.color;
      if (replaced || prior?.version !== version) {
        mesh.setMatrixAt(index, matrices[part.name]);
        this.matrixWritesThisFrame++;
        slots.matrixMin = Math.min(slots.matrixMin, index);
        slots.matrixMax = Math.max(slots.matrixMax, index);
      }
      if (replaced || prior?.color !== color) {
        mesh.setColorAt(index, part.cloth ? shirtTint : part.tint!);
        slots.colorMin = Math.min(slots.colorMin, index);
        slots.colorMax = Math.max(slots.colorMax, index);
      }
      slots.owners[index] = part;
      if (prior) {
        prior.index = index;
        prior.version = version;
        prior.color = color;
      } else part.submitted = { index, version, color };
    }
  }
  private renderCoarse(e: CharacterEntry, shirt: number): void {
    if (!e.coarse) {
      const parts = e.parts
        .filter(
          (p) =>
            ['torso', 'head', 'hair', 'thigh:left', 'thigh:right', 'weapon'].includes(p.name) ||
            (!!e.military && p.name === 'accessory'),
        )
        .map((part) => {
          const geometry =
            part.name === 'torso'
              ? `distant-${part.geometry}`
              : part.name.startsWith('thigh:')
                ? 'distant-leg'
                : part.geometry;
          return { ...part, geometry, mesh: this.meshes.get(geometry)!, submitted: undefined };
        });
      e.coarse = {
        parts,
        matrices: Object.fromEntries(parts.map((p) => [p.name, new THREE.Matrix4()])),
        version: 0,
      };
    }
    const c = e.coarse,
      scale = BODY_SCALE[e.style.build],
      hip = 0.825;
    const joint = (name: string, x: number, y: number, z: number, angle = 0) =>
      characterJoint(c.matrices[name], e.root, scale, x, y, z, angle);
    joint('torso', 0, hip, 0, WORKING.has(e.state) ? 0.09 : 0);
    joint('head', 0, hip + 0.455, 0);
    this.local
      .makeTranslation(0, HEAD_HEIGHT[e.style.head] - 0.29, 0)
      .scale(this.scale.set(HEAD_WIDTH[e.style.head] / 0.25, 1, 1.02));
    c.matrices.hair.multiplyMatrices(c.matrices.head, this.local);
    if (c.matrices.accessory) c.matrices.accessory.copy(c.matrices.torso);
    const swing = Math.sin(e.samplePhase) * e.sampleStride * 0.2;
    for (const [side, sign] of SIDES) {
      const name = `thigh:${side}`;
      joint(name, sign * 0.102, hip, 0);
      // Shear the tiny leg silhouette through the current gait. Its soles
      // stay on the support plane, with continuous root interpolation.
      const m = c.matrices[name].elements,
        r = e.root.elements,
        slope = ((sign * swing) / hip) * scale[1];
      m[4] += r[8] * slope;
      m[5] += r[9] * slope;
      m[6] += r[10] * slope;
    }
    if (e.military) {
      const aimed = ['aiming', 'firing', 'turret'].includes(e.state);
      joint('weapon', aimed ? 0.06 : 0, hip + (aimed ? 0.29 : 0.21), aimed ? -0.08 : 0, aimed ? 0 : -0.45);
    }
    this.coarseThisFrame++;
    this.submit(c.parts, c.matrices, ++c.version, shirt);
  }
  private updatePose(e: CharacterEntry): void {
    if (!e.dirty) {
      const d = e.translation;
      if (!d.x && !d.y && !d.z) return;
      // Unchanged seated/standing joints share the carrier's translation.
      // Keep every part and attachment exact without repeating limb IK.
      for (const part of e.parts) {
        const matrix = e.matrices[part.name].elements;
        matrix[12] += d.x;
        matrix[13] += d.y;
        matrix[14] += d.z;
      }
      d.set(0, 0, 0);
      e.poseVersion++;
      this.translatedThisFrame++;
      return;
    }
    e.dirty = false;
    e.translation.set(0, 0, 0);
    e.poseVersion++;
    this.posedThisFrame++;
    const { alpha, time, state, carrying } = e;
    const m = e.motion,
      phase = m.previousPhase + (m.phase - m.previousPhase) * alpha,
      stride = m.previousStride + (m.stride - m.previousStride) * alpha;
    this.posing = e;
    const seated = state === 'sitting' || state === 'resting',
      sit = seated ? (e.seatBlend ?? 1) : 0,
      work = WORKING.has(state);
    let hip =
      0.82 * (1 - sit) +
      (e.seatHeight === undefined ? 0.48 : e.seatHeight / BODY_SCALE[e.style.build][1]) * sit;
    const lean = work ? 0.09 : 0;
    if (!seated) {
      let sole = Infinity;
      for (const offset of OFFSETS) {
        const thigh = Math.sin(phase + offset) * stride * 0.46,
          knee = -Math.max(0, -Math.cos(phase + offset)) * stride * 0.58;
        sole = Math.min(sole, hip - Math.cos(thigh) * 0.37 - Math.cos(thigh + knee) * 0.37 - 0.083);
      }
      hip -= sole;
    }
    this.joint('torso', 0, hip, 0, lean);
    this.joint('head', 0, hip + 0.455, -(work ? 0.04 : 0), work ? 0.1 : 0);
    this.local
      .makeTranslation(0, HEAD_HEIGHT[e.style.head] - 0.29, 0)
      .scale(this.scale.set(HEAD_WIDTH[e.style.head] / 0.25, 1, 1.02));
    e.matrices.hair.multiplyMatrices(e.matrices.head, this.local);
    e.matrices.accessory.copy(!e.military && e.style.accessory === 3 ? e.matrices.head : e.matrices.torso);
    const rifle = e.military && !HOLSTERED.has(state);
    for (const [side, sign] of SIDES) {
      const swing = Math.sin(phase + (sign < 0 ? 0 : Math.PI)) * stride;
      const thigh = seated ? (Math.PI / 2) * sit : swing * 0.46,
        knee = seated
          ? (-Math.PI / 2) * sit
          : -Math.max(0, -Math.cos(phase + (sign < 0 ? 0 : Math.PI))) * stride * 0.58;
      const ankleY = hip - Math.cos(thigh) * 0.37 - Math.cos(thigh + knee) * 0.37,
        ankleZ = -Math.sin(thigh) * 0.37 - Math.sin(thigh + knee) * 0.37;
      this.joint(`thigh:${side}`, sign * 0.102, hip, 0, thigh);
      this.joint(
        `shin:${side}`,
        sign * 0.102,
        hip - Math.cos(thigh) * 0.37,
        -Math.sin(thigh) * 0.37,
        thigh + knee,
      );
      this.joint(`shoe:${side}`, sign * 0.102, ankleY - 0.065, ankleZ, 0);
      if (rifle) continue;
      const upper =
        state === 'parachuting' || state === 'surrendering'
          ? 2.8
          : state === 'stretching'
            ? 1.7 + Math.sin(time * 0.8 + sign * 0.4) * 0.55
            : carrying
              ? 0.7
              : work
                ? 0.65 + Math.sin(time * 3.5 + sign) * 0.045
                : -swing * 0.43;
      const fore =
        state === 'parachuting' || state === 'surrendering'
          ? -0.45
          : carrying
            ? 0.62
            : work
              ? 0.72 + Math.sin(time * 3.5 + sign) * 0.06
              : 0.08;
      const shoulderY = hip + 0.37,
        shoulderX = sign * 0.26;
      this.joint(`upper-arm:${side}`, shoulderX, shoulderY, 0, upper);
      this.joint(
        `forearm:${side}`,
        shoulderX,
        shoulderY - Math.cos(upper) * 0.275,
        -Math.sin(upper) * 0.275,
        upper + fore,
      );
      this.joint(
        `hand:${side}`,
        shoulderX,
        shoulderY - Math.cos(upper) * 0.275 - Math.cos(upper + fore) * 0.25,
        -Math.sin(upper) * 0.275 - Math.sin(upper + fore) * 0.25,
        upper + fore,
      );
    }
    if (e.military) {
      if (HOLSTERED.has(state)) {
        this.joint('weapon', 0.17, hip + 0.2, 0.22, 1.25);
        return;
      }
      const pitch = state === 'aiming' || state === 'firing' || state === 'turret' ? 0 : -1.1;
      const recoil = state === 'firing' ? Math.max(0, Math.sin(time * 36)) * 0.026 : 0;
      const height = hip * BODY_SCALE[e.style.build][1],
        root = e.root.elements;
      for (const [name, local] of this.riflePose(e.style.build, pitch)) {
        const matrix = e.matrices[name];
        matrix.multiplyMatrices(e.root, local);
        matrix.elements[12] += root[4] * height;
        matrix.elements[13] += root[5] * height;
        matrix.elements[14] += root[6] * height;
        matrix.elements[12] += root[8] * recoil;
        matrix.elements[13] += root[9] * recoil;
        matrix.elements[14] += root[10] * recoil;
      }
    }
  }
  /** The same two-bone grip shape is shared by body proportion and rifle pitch.
   * Hip motion and carrier rotation still apply continuously to every joint. */
  private riflePose(build: number, pitch: number): [CharacterPart, THREE.Matrix4][] {
    const key = `${build}:${pitch}`,
      prior = this.rifleArms.get(key);
    if (prior) return prior;
    const scale = BODY_SCALE[build],
      [width, height, depth] = scale,
      grip = new THREE.Vector3(
        (pitch === 0 ? 0.08 : -0.055) * width,
        (pitch === 0 ? 0.3 : 0.34) * height,
        (pitch === 0 ? -0.06 : -0.09) * depth,
      ),
      rotation = new THREE.Quaternion().setFromEuler(new THREE.Euler(pitch, pitch === 0 ? 0 : -0.38, 0)),
      weapon = new THREE.Matrix4().compose(
        grip,
        rotation,
        new THREE.Vector3(width * 0.82, height * 0.82, depth * 0.82),
      ),
      result: [CharacterPart, THREE.Matrix4][] = [['weapon', weapon]];
    for (const [side, sign] of SIDES) {
      const shoulder = new THREE.Vector3(sign * 0.26 * width, 0.37 * height, 0),
        hand = new THREE.Vector3(
          0,
          side === 'left' ? -0.022 : -0.087,
          side === 'left' ? -0.3 : 0.022,
        ).applyMatrix4(weapon);
      const direction = hand.clone().sub(shoulder),
        upperLength = 0.275 * height,
        foreLength = 0.25 * height,
        length = Math.min(upperLength + foreLength - 0.0001, Math.max(0.001, direction.length()));
      direction.normalize();
      const along = (upperLength ** 2 - foreLength ** 2 + length ** 2) / (2 * length),
        outward = new THREE.Vector3(sign * 0.32, -1, 0.12);
      outward.addScaledVector(direction, -outward.dot(direction)).normalize();
      const elbow = shoulder
        .clone()
        .addScaledVector(direction, along)
        .addScaledVector(outward, Math.sqrt(Math.max(0, upperLength ** 2 - along ** 2)));
      for (const [name, a, b] of [
        [`upper-arm:${side}`, shoulder, elbow],
        [`forearm:${side}`, elbow, hand],
      ] as [CharacterPart, THREE.Vector3, THREE.Vector3][]) {
        const q = new THREE.Quaternion().setFromUnitVectors(
          new THREE.Vector3(0, -1, 0),
          b.clone().sub(a).normalize(),
        );
        result.push([name, new THREE.Matrix4().compose(a, q, new THREE.Vector3(width, height, depth))]);
      }
      const palm = new THREE.Matrix4();
      characterJoint(
        palm,
        new THREE.Matrix4(),
        scale,
        hand.x / width,
        hand.y / height,
        hand.z / height,
        Math.PI / 2 + pitch,
      );
      result.push([`hand:${side}`, palm]);
    }
    this.rifleArms.set(key, result);
    return result;
  }
  finish(): void {
    for (const mesh of this.meshes.values()) {
      if (!mesh.count) continue;
      const slots = this.submissions.get(mesh)!;
      mesh.instanceMatrix.clearUpdateRanges();
      if (slots.matrixMax >= slots.matrixMin) {
        mesh.instanceMatrix.addUpdateRange(
          slots.matrixMin * 16,
          (slots.matrixMax - slots.matrixMin + 1) * 16,
        );
        mesh.instanceMatrix.needsUpdate = true;
      }
      if (mesh.instanceColor) {
        mesh.instanceColor.clearUpdateRanges();
        if (slots.colorMax >= slots.colorMin) {
          mesh.instanceColor.addUpdateRange(slots.colorMin * 3, (slots.colorMax - slots.colorMin + 1) * 3);
          mesh.instanceColor.needsUpdate = true;
        }
      }
    }
  }
  clear(): void {
    this.entries.clear();
    this.used.clear();
    for (const s of this.submissions.values()) s.owners.length = 0;
    this.begin();
  }
  dispose(): void {
    this.clear();
    for (const mesh of this.meshes.values()) {
      this.scene.remove(mesh);
      mesh.geometry.dispose();
      mesh.dispose();
    }
    this.meshes.clear();
  }
}
